// Static content for the /extension-rules page. Split out of ExtensionRules.jsx
// so the component file focuses on layout & JSX and the copy is easy to review
// in isolation for SEO / editorial changes.

export const RULES_IMG = {
  standard: "https://static.prod-images.emergentagent.com/jobs/3e047fd2-aa5b-47d3-8929-6e98c2365ce5/images/5a61e71fb106e3f98331a2d05fbfdde047f49fae44ba91ffbd6ebdd351b3d97f.png",
  prior: "https://static.prod-images.emergentagent.com/jobs/3e047fd2-aa5b-47d3-8929-6e98c2365ce5/images/8e1d554f8994dcc31bf6155b9b25f8847560219c6f798c7b5bb155be0bc16450.png",
  planning: "https://static.prod-images.emergentagent.com/jobs/3e047fd2-aa5b-47d3-8929-6e98c2365ce5/images/2747fba992b14d55c1294067e8504705d556f8384712003a639eac779c180978.png",
};

export const ROUTES = [
  {
    key: "standard", img: RULES_IMG.standard, badge: "No approval needed",
    title: "Standard Permitted Development",
    depth: "Up to 4m · detached  |  Up to 3m · semi & terraced",
    points: [
      "The quickest route — no planning application required",
      "Single-storey, rear only, max 4m high (3m eaves within 2m of a boundary)",
      "Must not cover more than 50% of the land around the original house",
    ],
  },
  {
    key: "prior", img: RULES_IMG.prior, badge: "Prior approval required",
    title: "Larger Home Extension (Prior Approval)",
    depth: "Up to 8m · detached  |  Up to 6m · semi & terraced",
    points: [
      "Lets you go roughly twice as deep, still under Permitted Development",
      "We submit a Prior Approval application; the council notifies your neighbours",
      "21-day neighbour consultation — if there are no valid objections, you're cleared to build",
    ],
  },
  {
    key: "planning", img: RULES_IMG.planning, badge: "Full planning permission",
    title: "Full Planning Permission",
    depth: "Beyond PD limits · two-storey · wrap-around · designated land",
    points: [
      "Needed when you exceed the 6m/8m limits, go two-storey or wrap-around",
      "Also required in conservation areas / where an Article 4 direction applies",
      "Full design drawings submitted; typically an 8-week decision",
    ],
  },
];

export const STANDARD_LIMITS = [
  ["Detached house", "4 metres", "8 metres"],
  ["Semi-detached house", "3 metres", "6 metres"],
  ["Terraced house", "3 metres", "6 metres"],
];

export const CONDITIONS = [
  "Single-storey only and to the rear of the original house",
  "Maximum height 4m — and no more than 3m at the eaves if within 2m of a boundary",
  "No wider than half the width of the original house",
  "Extension + outbuildings must not cover more than 50% of the garden",
  "Materials similar in appearance to the existing house",
];

export const PRIOR_APPROVAL_STEPS = [
  ["01", "We apply", "We prepare and submit the prior-approval application to your local council on your behalf."],
  ["02", "Neighbours notified", "The council writes to the immediately adjoining neighbours about the proposal."],
  ["03", "21-day window", "Neighbours have 21 days to raise any concerns about loss of light, privacy or amenity."],
  ["04", "Decision", "No valid objections? You're clear to build. If concerns are raised, the council weighs the impact."],
];

export const RULES_SEO = {
  title: "Permitted Development & Extension Rules 2026 — How Big Can You Build? | The Extension Works",
  description: "Plain-English guide to UK rear extension rules: Permitted Development depth limits (3m/4m), the Larger Home Extension prior-approval route (6m/8m) and when full planning permission is needed.",
  canonical: "https://theextensionworks.co.uk/extension-rules",
  image: "https://theextensionworks.co.uk/brand/og-image.png",
  jsonLd: {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "mainEntity": [
      { "@type": "Question", "name": "How deep can a single-storey rear extension be under Permitted Development?", "acceptedAnswer": { "@type": "Answer", "text": "Under standard Permitted Development a single-storey rear extension can extend up to 4 metres from the original rear wall on a detached house, or 3 metres on a semi-detached or terraced house, without a planning application." } },
      { "@type": "Question", "name": "What is the Larger Home Extension (prior approval) scheme?", "acceptedAnswer": { "@type": "Answer", "text": "The Larger Home Extension scheme lets you build a single-storey rear extension up to 8 metres deep on a detached house, or 6 metres on a semi-detached or terraced house, with a simple prior-approval application and a 21-day neighbour consultation — still without full planning permission." } },
      { "@type": "Question", "name": "When do I need full planning permission for an extension?", "acceptedAnswer": { "@type": "Answer", "text": "Full planning permission is needed if you exceed the 6m/8m limits, build two-storey or wrap-around, or your property is a flat, maisonette or listed building, sits on designated land such as a conservation area or National Park, or has an Article 4 direction removing permitted development rights." } },
      { "@type": "Question", "name": "What conditions must a Permitted Development extension meet?", "acceptedAnswer": { "@type": "Answer", "text": "It must be single-storey and to the rear of the original house, no more than 4m high (3m at the eaves within 2m of a boundary), no wider than half the original house, must not cover more than 50% of the garden, and use materials similar in appearance to the existing house." } },
      { "@type": "Question", "name": "How much does a single-storey rear extension cost?", "acceptedAnswer": { "@type": "Answer", "text": "Single-storey rear extensions with The Extension Works start from around £29,950 for a fixed, VAT-inclusive price covering design, structure, Building Control and the build. Use our instant quote tool for a precise fixed price for your exact specification." } }
    ]
  }
};
